import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import axios from 'axios';
import mermaid from 'mermaid';
import { toast } from 'react-toastify';
import { FaTimes, FaExpandArrowsAlt, FaCompressArrowsAlt, FaSearchPlus, FaSearchMinus, FaCompress, FaDownload, FaCode, FaFileImage } from 'react-icons/fa';
import styles from './ERDModal.module.css';
import { fetchRelationships, analyzeRelationships } from '../services/assistantClient';

import { toCssPx } from '../utils/appZoom';
/**
 * Mermaid 설정 — **한 곳에서만 만든다.**
 *
 * 예전에는 같은 객체가 두 군데(초기화·렌더 직전)에 복사돼 있었다. 테마를 넣으면서 한 곳만
 * 고치면 "처음엔 어두운데 필터를 바꾸면 하얘지는" 화면이 된다.
 *
 * `maxTextSize` 를 크게 두는 이유: mermaid 기본값 50,000자를 넘으면 **예외를 던지지 않고
 * 빈 SVG 를 준다**(실측: 230표 = 146,410자 → 6KB 빈 SVG). 백지의 원인이 안 보인다.
 */
const mermaidConfig = (dark) => ({
    startOnLoad: false,
    maxTextSize: 1000000,
    maxEdges: 2000,
    // 다이어그램도 화면 테마를 따른다. 모달 CSS 만 어둡게 하면 **검은 창에 흰 그림**이 된다.
    theme: dark ? 'dark' : 'default',
    // **strict** — `loose` 는 라벨의 HTML 을 그대로 두고 클릭 콜백을 허용한다. ER 다이어그램의
    // 글자는 DB 의 테이블·컬럼 이름(safeEntityName 이 걸러 주지만)이고 결과 SVG 를
    // innerHTML 로 꽂으므로, 필요 없는 권한은 처음부터 안 준다. ER 은 콜백을 쓰지 않는다.
    securityLevel: 'strict',
    er: {
        layoutDirection: 'TB',
        useMaxWidth: true,
        diagramPadding: 30,
        entityPadding: 20,
        fontSize: 12,
        minEntityWidth: 120,
        minEntityHeight: 80,
    },
    flowchart: { rankSpacing: 60, nodeSpacing: 60, curve: 'basis' },
    logLevel: 'error',
    suppressErrorRendering: false,
});

/** 지금 화면이 다크인가 — App 이 `document.documentElement` 에 data-theme 을 찍는다. */
const isDarkTheme = () => (typeof document !== 'undefined'
    && document.documentElement.getAttribute('data-theme') === 'dark');

// axiosInstance 생성 (스키마 조회용 - 인증 없음)
const axiosInstance = axios.create({
    baseURL: '/sql-developer',
    headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
    },
    timeout: 15000,
    validateStatus: function (status) {
        return status < 500;
    }
});

const ERDModal = ({ isOpen, onClose, schemaName, connectionData }) => {
    const { t } = useTranslation();
    // Redux 상태에서 스키마 목록 가져오기
    const connectionsFromRedux = useSelector(state => state.schema?.connections || []);
    /**
     * 스키마 목록은 **이 ERD 가 열린 그 연결**의 것이어야 한다.
     *
     * 예전에는 이름에 'Oracle' 이 들어간 연결을 찾고, 없으면 목록의 첫 번째로 떨어졌다.
     * 첫 번째는 언제나 **내장 SQLite**(성능 수집 DB)라, 이름에 'Oracle' 이 없는 연결
     * (교육DB 등)로 ERD 를 열면 스키마 목록에 SQLite 의 `main` 이 떴다 — 고르면 당연히
     * 아무것도 없다. 이름으로 DB 를 알아내려 한 것이 애초에 틀렸다.
     */
    const activeConnection = connectionsFromRedux.find(conn => conn.id === connectionData?.id)
        || connectionData
        || null;
    const schemasFromRedux = activeConnection?.children || [];

    const [erdDiagram, setErdDiagram] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(100);
    const [autoFit, setAutoFit] = useState(true);
    const zoomLevelRef = useRef(100);
    const autoFitRef = useRef(true);

    // 패닝 상태를 전역으로 관리
    const currentTranslateXRef = useRef(0);
    const currentTranslateYRef = useRef(0);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTables, setSelectedTables] = useState(new Set());
    const [allTables, setAllTables] = useState([]);
    const [originalErdData, setOriginalErdData] = useState(null);
    const [selectedSchema, setSelectedSchema] = useState(schemaName);
    const [availableSchemas, setAvailableSchemas] = useState([]);
    const [showSourceCode, setShowSourceCode] = useState(false);

    // 관계 목록 관련 상태
    const [allRelationships, setAllRelationships] = useState([]);
    const [selectedRelationships, setSelectedRelationships] = useState(new Set());

    // 드래그 상태
    const [draggedItem, setDraggedItem] = useState(null);
    const [draggedType, setDraggedType] = useState(null); // 'table' or 'relationship'
    const diagramRef = useRef(null);
    const interactionCleanupRef = useRef(null);   // 패닝/휠 리스너 해제 함수
    // 표시 수준 — 렌더 비용은 표 수가 아니라 '컬럼 총량' 에 비례한다 (실측).
    //   표300 컬럼전체 184KB → 16.8초 / 표300 키컬럼만 20KB → 1.6초.
    // 그래서 큰 스키마는 컬럼을 줄여서 그린다. 표는 전부 보여준다.
    const [detailLevel, setDetailLevel] = useState('full');   // full | keys | none
    const detailLevelRef = useRef('full');
    const diagramId = `erd-diagram-${Date.now()}`;

    // 디버그 로그 상태 (주석처리)
    // const [debugLogs, setDebugLogs] = useState([]);
    // const addDebugLog = (message, data = null) => {
    //     const timestamp = new Date().toLocaleTimeString();
    //     const logEntry = { timestamp, message, data };
    //     setDebugLogs(prev => [...prev.slice(-9), logEntry]); // 최근 10개만 유지
    //     console.log(`[DEBUG ${timestamp}]`, message, data);
    // };
    const addDebugLog = () => {}; // 빈 함수로 대체

    // Mermaid 초기화 + **테마 추적.** 라이트/다크를 바꾸면 다시 그린다 — 안 그리면
    // 테마를 바꾼 뒤 새로 열어야만 색이 맞아 "가끔 하얗게 나온다" 로 보인다.
    const [dark, setDark] = useState(isDarkTheme());
    useEffect(() => {
        mermaid.initialize(mermaidConfig(dark));
    }, [dark]);
    useEffect(() => {
        const ob = new MutationObserver(() => setDark(isDarkTheme()));
        ob.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
        return () => ob.disconnect();
    }, []);

    // Modal 열릴 때 전역 상태 초기화 필요시 여기에 작성
    useEffect(() => {
        if (isOpen) {
            console.log('ERD Modal opened');
        }
    }, [isOpen]);

    // ERD 데이터 가져오기
    useEffect(() => {
        console.log('ERD Modal useEffect triggered:', { isOpen, schemaName, connectionId: connectionData?.id });
        if (isOpen && schemaName && connectionData?.id) {
            console.log('Fetching ERD data for schema:', schemaName);
            fetchERDData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, schemaName, connectionData?.id]);

    // 모달이 열렸을 때 body 스크롤 방지
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    // 다이어그램 렌더링
    useEffect(() => {
        console.log('Diagram rendering useEffect triggered:', {
            erdDiagram: !!erdDiagram,
            diagramRefCurrent: !!diagramRef.current,
            erdDiagramLength: erdDiagram?.length
        });

        if (erdDiagram && diagramRef.current) {
            console.log('Calling renderDiagram()');
            // 약간의 지연을 두어 DOM이 완전히 준비되도록 함
            const timeoutId = setTimeout(() => {
                renderDiagram();
            }, 50);

            return () => clearTimeout(timeoutId);
        } else {
            console.log('Skipping renderDiagram - missing prerequisites');
        }
        // `dark` 도 의존이다 — 테마가 바뀌면 **다시 그려야** 색이 따라온다.
        // (mermaid.initialize 만으로는 이미 그려진 SVG 가 안 바뀐다)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erdDiagram, dark]);

    // Redux 상태 변경 시 스키마 목록 업데이트 (안정화)
    useEffect(() => {
        // 기본값들이 변경되지 않았다면 업데이트 스킵
        if (schemasFromRedux && schemasFromRedux.length > 0) {
            const schemaNames = schemasFromRedux.map(schema => schema.name);
            setAvailableSchemas(prev => {
                // 이전 값과 같으면 상태 업데이트 하지 않음
                if (JSON.stringify(prev) !== JSON.stringify(schemaNames)) {
                    return schemaNames;
                }
                return prev;
            });
        } else if (connectionData && connectionData.schemas) {
            setAvailableSchemas(prev => {
                if (JSON.stringify(prev) !== JSON.stringify(connectionData.schemas)) {
                    return connectionData.schemas;
                }
                return prev;
            });
        } else if (schemaName) {
            setAvailableSchemas(prev => {
                const newValue = [schemaName];
                if (JSON.stringify(prev) !== JSON.stringify(newValue)) {
                    return newValue;
                }
                return prev;
            });
        }
    }, [schemasFromRedux, connectionData, schemaName]);


    // 스키마 변경 핸들러
    const handleSchemaChange = (newSchema) => {
        if (newSchema !== selectedSchema) {
            setSelectedSchema(newSchema);
            setSearchTerm(''); // 검색어 초기화
            setSelectedTables(new Set()); // 선택된 테이블 초기화
            setAllTables([]); // 테이블 목록 초기화

            // 새로운 스키마의 ERD 데이터 가져오기
            fetchERDDataForSchema(newSchema);
        }
    };

    // 테이블 포커스 기능 - 간단하고 확실한 방법으로 재작성
    const handleTableFocus = (tableName) => {
        addDebugLog('TABLE FOCUS START', { tableName });

        if (!diagramRef.current) {
            addDebugLog('ERROR: diagramRef is null');
            return;
        }

        const svgElement = diagramRef.current.querySelector('svg');
        if (!svgElement) {
            addDebugLog('ERROR: SVG element not found');
            return;
        }

        // 기존 강조 효과 모두 제거
        const allGroups = svgElement.querySelectorAll('g');
        allGroups.forEach(group => {
            group.style.filter = '';
            group.style.transition = '';
        });

        // 테이블 이름을 포함하는 텍스트 요소 찾기 (여러 방법 시도)
        const findTableText = () => {
            const allTextElements = svgElement.querySelectorAll('text, tspan, foreignObject');

            console.log('Available text elements:',
                Array.from(allTextElements).map(el => el.textContent?.trim()).filter(Boolean)
            );

            // 1. 정확한 매치
            for (const textEl of allTextElements) {
                const content = textEl.textContent?.trim();
                if (content === tableName) {
                    console.log('Found exact match:', content);
                    return textEl;
                }
            }

            // 2. safeEntityName 변환 후 매치
            const safeName = tableName.replace(/[^a-zA-Z0-9가-힣_]/g, '_');
            for (const textEl of allTextElements) {
                const content = textEl.textContent?.trim();
                if (content === safeName) {
                    console.log('Found safe name match:', content);
                    return textEl;
                }
            }

            // 3. 부분 매치
            for (const textEl of allTextElements) {
                const content = textEl.textContent?.trim();
                if (content && content.includes(tableName)) {
                    console.log('Found partial match:', content);
                    return textEl;
                }
            }

            console.log('No text element found for:', tableName);
            return null;
        };

        const textElement = findTableText();
        if (!textElement) {
            addDebugLog('ERROR: Text element not found', { tableName });
            return;
        }
        addDebugLog('Found text element', { text: textElement.textContent });

        // 텍스트를 포함하는 가장 큰 그룹 찾기 (테이블 전체)
        let targetGroup = textElement.closest('g');
        if (!targetGroup) {
            console.error('No group found for text element');
            return;
        }

        // 상위 그룹들을 확인하여 더 큰 그룹이 있으면 사용
        let parent = targetGroup.parentElement;
        while (parent && parent.tagName === 'g') {
            try {
                const parentBBox = parent.getBBox();
                const currentBBox = targetGroup.getBBox();

                // 부모가 현재보다 크지만 전체 SVG보다는 작으면 사용
                if (parentBBox.width > currentBBox.width &&
                    parentBBox.height > currentBBox.height &&
                    parentBBox.width < svgElement.getBBox().width * 0.9) {
                    targetGroup = parent;
                    parent = parent.parentElement;
                } else {
                    break;
                }
            } catch (e) {
                console.log('getBBox error:', e);
                break;
            }
        }

        console.log('Target group selected:', {
            id: targetGroup.id || 'no-id',
            className: targetGroup.className?.baseVal || 'no-class'
        });

        try {
            // 현재 줌 레벨 가져오기
            const currentZoomLevel = zoomLevelRef.current || 100;
            const currentScale = currentZoomLevel / 100;

            addDebugLog('Zoom info', {
                zoomLevel: currentZoomLevel,
                scale: currentScale,
                autoFit: autoFitRef.current
            });

            // autoFit 모드 해제
            setAutoFit(false);

            // 1. 현재 화면상 위치 저장 (transform 적용된 상태)
            const targetRectBefore = targetGroup.getBoundingClientRect();
            const containerRect = diagramRef.current.getBoundingClientRect();

            // 테이블의 중심점 (현재 화면 좌표)
            const targetScreenCenterX = targetRectBefore.left + targetRectBefore.width / 2;
            const targetScreenCenterY = targetRectBefore.top + targetRectBefore.height / 2;

            // 컨테이너의 중심점 (화면 좌표)
            const containerScreenCenterX = containerRect.left + containerRect.width / 2;
            const containerScreenCenterY = containerRect.top + containerRect.height / 2;

            // 화면상에서 이동해야 할 거리 (픽셀)
            // 화면 px → CSS px(translate 단위, 웹의 화면 확대 utils/appZoom)
            const screenDeltaX = toCssPx(containerScreenCenterX - targetScreenCenterX);
            const screenDeltaY = toCssPx(containerScreenCenterY - targetScreenCenterY);

            addDebugLog('Screen positions', {
                'targetScreen': `${targetScreenCenterX.toFixed(0)},${targetScreenCenterY.toFixed(0)}`,
                'containerScreen': `${containerScreenCenterX.toFixed(0)},${containerScreenCenterY.toFixed(0)}`,
                'screenDelta': `${screenDeltaX.toFixed(0)},${screenDeltaY.toFixed(0)}`
            });

            // 2. 현재 translate 값에 화면 델타를 더함
            const oldTranslateX = currentTranslateXRef.current;
            const oldTranslateY = currentTranslateYRef.current;
            const translateX = oldTranslateX + (screenDeltaX / currentScale);
            const translateY = oldTranslateY + (screenDeltaY / currentScale);

            addDebugLog('Translate calculation', {
                'oldTranslate': `${oldTranslateX.toFixed(0)},${oldTranslateY.toFixed(0)}`,
                'screenDelta/scale': `${(screenDeltaX/currentScale).toFixed(0)},${(screenDeltaY/currentScale).toFixed(0)}`,
                'newTranslate': `${translateX.toFixed(0)},${translateY.toFixed(0)}`
            });


            // 전역 패닝 상태 업데이트
            currentTranslateXRef.current = translateX;
            currentTranslateYRef.current = translateY;

            // transform 적용 (현재 줌 레벨 유지)
            const newTransform = `scale(${currentScale}) translate(${translateX}px, ${translateY}px)`;
            svgElement.style.transition = 'transform 0.3s ease-out';
            svgElement.style.transform = newTransform;

            addDebugLog('Transform applied', {
                transform: newTransform
            });

            // 강조 효과
            targetGroup.style.filter = 'drop-shadow(0 0 10px rgba(51, 51, 51, 0.6))';
            targetGroup.style.transition = 'filter 0.3s ease';

            // 텍스트 강조
            textElement.style.fill = '#333333';
            textElement.style.fontWeight = 'bold';

            // 실제 적용된 filter 값 확인
            setTimeout(() => {
                const appliedFilter = window.getComputedStyle(targetGroup).filter;
                addDebugLog('Table highlight check', {
                    filter: appliedFilter,
                    groupTag: targetGroup.tagName
                });
            }, 100);

            // 효과 제거 타이머
            setTimeout(() => {
                svgElement.style.transition = '';
                targetGroup.style.filter = '';
                targetGroup.style.transition = '';
                textElement.style.fill = '';
                textElement.style.fontWeight = '';
            }, 2000);

            console.log('Table focus completed successfully');

        } catch (error) {
            console.error('Error in table focus:', error);
        }
    };

    // 특정 스키마의 ERD 데이터 가져오기
    const fetchERDDataForSchema = async (schema) => {
        setLoading(true);
        setError(null);

        const connId = connectionData?.id;
        if (!connId) {
            setError(t('common:erd.err.noConnId'));
            setLoading(false);
            return;
        }
        try {
            console.log('Making ERD API request for schema:', schema, 'connectionId:', connId);
            const url = `/schema-erd/${encodeURIComponent(schema)}?connectionId=${encodeURIComponent(connId)}`;
            const response = await axiosInstance.get(url);
            console.log('ERD API response:', response.data);

            if (response.data.success) {
                console.log('ERD data received:', response.data.erdData);

                // 원본 ERD 데이터 저장
                setOriginalErdData(response.data.erdData);
                // 저장된 추정 관계도 함께 읽는다 — 없으면 조용히 빈 목록이다(ERD 를 막지 않는다)
                loadInferred(response.data.erdData?.schemaName || selectedSchema);

                // 테이블 목록 설정 및 모든 테이블 초기 선택
                if (response.data.erdData && response.data.erdData.tables) {
                    const tableNames = response.data.erdData.tables.map(table => table.tableName);
                    setAllTables(tableNames);
                    // 표는 전부 보여주고, 대신 컬럼 표시 수준을 낮춰 렌더를 빠르게 한다.
                    // (표를 잘라내면 관계선이 끊겨 ERD 의 쓸모가 준다.)
                    setSelectedTables(new Set(tableNames));
                    // 컬럼 수로 어림하지 않고 실제 생성 코드 크기로 고른다 — 스키마마다
                    // 컬럼 폭이 달라 어림값은 빗나간다. 생성은 20ms 내외라 세 번 해도 싸다.
                    // 렌더 비용 실측 ≈ 코드 1KB 당 80ms → 예산 30KB ≈ 2.4초.
                    const CODE_BUDGET = 30 * 1024;
                    let auto = 'full';
                    for (const lvl of ['full', 'keys', 'none']) {
                        detailLevelRef.current = lvl;
                        auto = lvl;
                        if (generateMermaidERD(response.data.erdData).length <= CODE_BUDGET) break;
                    }
                    detailLevelRef.current = auto;
                    setDetailLevel(auto);
                    if (auto !== 'full') {
                        const totalCols = response.data.erdData.tables
                            .reduce((acc, x) => acc + ((x.columns && x.columns.length) || 0), 0);
                        toast.info(t('common:erd.autoDetail', { tables: tableNames.length, columns: totalCols }));
                    }
                    console.log('Available tables:', tableNames);
                }

                // 관계 목록 설정 및 모든 관계 초기 선택
                if (response.data.erdData && response.data.erdData.relationships) {
                    const relationships = response.data.erdData.relationships.map((rel, index) => ({
                        id: `rel_${index}`,
                        parentTable: rel.parentTable,
                        childTable: rel.childTable,
                        relationshipName: rel.relationshipName || 'references',
                        displayName: `${rel.parentTable} → ${rel.childTable}`
                    }));
                    setAllRelationships(relationships);
                    setSelectedRelationships(new Set(relationships.map(rel => rel.id))); // 모든 관계 초기 선택
                    console.log('Available relationships:', relationships);
                }

                const mermaidCode = generateMermaidERD(response.data.erdData);
                console.log('Setting ERD diagram code');
                setErdDiagram(mermaidCode);

                // State 업데이트 후 바로 렌더링 시도
                setTimeout(() => {
                    if (diagramRef.current) {
                        console.log('Forcing diagram render after state update');
                        renderDiagramWithCode(mermaidCode);
                    }
                }, 100);
            } else {
                throw new Error(response.data.error || t('common:erd.loadFailed'));
            }
        } catch (err) {
            console.error('ERD 데이터 조회 오류:', err);
            let errorMessage = t('common:erd.loadFailed');

            if (err.response) {
                errorMessage = err.response.data?.error || t('common:erd.err.server', { status: err.response.status });
            } else if (err.request) {
                errorMessage = t('common:erd.err.noServer');
            } else {
                errorMessage = err.message || t('common:erd.err.request');
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const fetchERDData = async () => {
        setLoading(true);
        setError(null);

        const connId = connectionData?.id;
        if (!connId) {
            setError(t('common:erd.err.noConnId'));
            setLoading(false);
            return;
        }
        try {
            console.log('Making ERD API request for schema:', schemaName, 'connectionId:', connId);
            const url = `/schema-erd/${encodeURIComponent(schemaName)}?connectionId=${encodeURIComponent(connId)}`;
            const response = await axiosInstance.get(url);
            console.log('ERD API response:', response.data);

            if (response.data.success) {
                console.log('ERD data received:', response.data.erdData);

                // 원본 ERD 데이터 저장
                setOriginalErdData(response.data.erdData);
                // 저장된 추정 관계도 함께 읽는다 — 없으면 조용히 빈 목록이다(ERD 를 막지 않는다)
                loadInferred(response.data.erdData?.schemaName || selectedSchema);

                // 테이블 목록 설정 및 모든 테이블 초기 선택
                if (response.data.erdData && response.data.erdData.tables) {
                    const tableNames = response.data.erdData.tables.map(table => table.tableName);
                    setAllTables(tableNames);
                    setSelectedTables(new Set(tableNames)); // 모든 테이블 초기 선택
                    console.log('Available tables:', tableNames);
                }

                // 관계 목록 설정 및 모든 관계 초기 선택
                if (response.data.erdData && response.data.erdData.relationships) {
                    const relationships = response.data.erdData.relationships.map((rel, index) => ({
                        id: `rel_${index}`,
                        parentTable: rel.parentTable,
                        childTable: rel.childTable,
                        relationshipName: rel.relationshipName || 'references',
                        displayName: `${rel.parentTable} → ${rel.childTable}`
                    }));
                    setAllRelationships(relationships);
                    setSelectedRelationships(new Set(relationships.map(rel => rel.id))); // 모든 관계 초기 선택
                    console.log('Available relationships:', relationships);
                }

                const mermaidCode = generateMermaidERD(response.data.erdData);
                console.log('Setting ERD diagram code');
                setErdDiagram(mermaidCode);

                // State 업데이트 후 바로 렌더링 시도
                setTimeout(() => {
                    if (diagramRef.current) {
                        console.log('Forcing diagram render after state update');
                        renderDiagramWithCode(mermaidCode);
                    }
                }, 100);
            } else {
                throw new Error(response.data.error || t('common:erd.loadFailed'));
            }
        } catch (err) {
            console.error('ERD 데이터 조회 오류:', err);
            let errorMessage = t('common:erd.loadFailed');

            if (err.response) {
                // 서버에서 응답을 받았지만 에러 상태
                errorMessage = err.response.data?.error || t('common:erd.err.server', { status: err.response.status });
            } else if (err.request) {
                // 요청은 했지만 응답을 받지 못함
                errorMessage = t('common:erd.err.network');
            } else {
                // 요청 설정 중 에러
                errorMessage = err.message || t('common:erd.err.request');
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const generateMermaidERD = (erdData, filteredTableNames = null, filteredRelationshipIds = null) => {
        if (!erdData || !erdData.tables || erdData.tables.length === 0) {
            return `erDiagram\n    EMPTY["${t('common:erd.mermaid.noTables')}"]`;
        }

        // 필터링할 테이블 이름이 제공되면 해당 테이블만 사용
        const tablesToShow = filteredTableNames ?
            erdData.tables.filter(table => filteredTableNames.has(table.tableName)) :
            erdData.tables;

        if (tablesToShow.length === 0) {
            return `erDiagram\n    EMPTY["${t('common:erd.mermaid.noneSelected')}"]`;
        }

        // 데이터 타입을 Mermaid에서 안전한 형태로 변환 (간단한 형식)
        const sanitizeDataType = (dataType) => {
            if (!dataType || typeof dataType !== 'string') {
                return 'string'; // 기본값
            }

            let cleanType = dataType.trim().toUpperCase();

            // Oracle 복잡한 타입들을 단순화 - 괄호와 내용 제거
            if (cleanType.includes('(')) {
                // NUMBER(10,2) -> NUMBER, VARCHAR2(100) -> VARCHAR2
                cleanType = cleanType.split('(')[0];
            }

            // 특수 타입들을 기본 타입으로 변환
            const typeMap = {
                'TEXTDOC_TAB': 'string',
                'ADHEADER_TYP': 'string',
                'BFILE': 'string',
                'NCLOB': 'string',
                'CLOB': 'string',
                'BLOB': 'string',
                'VARCHAR2': 'string',
                'CHAR': 'string',
                'NCHAR': 'string',
                'NVARCHAR2': 'string',
                'NUMBER': 'int',
                'INTEGER': 'int',
                'DECIMAL': 'int',
                'FLOAT': 'int',
                'DATE': 'string',
                'TIMESTAMP': 'string',
                'RAW': 'string'
            };

            const finalType = typeMap[cleanType] || 'string';

            // 유효한 Mermaid 타입인지 확인
            const validTypes = ['string', 'int', 'float', 'boolean', 'date'];
            return validTypes.includes(finalType) ? finalType : 'string';
        };

        // 엔터티명을 안전하게 처리하는 함수 (특수문자 제거)
        const safeEntityName = (name) => {
            if (!name || typeof name !== 'string') {
                return 'UNKNOWN_TABLE';
            }

            // Mermaid에서 허용되지 않는 특수문자 제거 ($, #, *, etc.)
            // 한글은 유지하고 특수문자만 언더스코어로 변경
            let safeName = name.trim().replace(/[^a-zA-Z0-9가-힣_]/g, '_');

            // 숫자로 시작하면 T_를 앞에 추가
            if (/^\d/.test(safeName)) {
                safeName = 'T_' + safeName;
            }

            // 너무 긴 이름은 잘라내기
            if (safeName.length > 50) {
                safeName = safeName.substring(0, 47) + '___';
            }

            return safeName || 'UNKNOWN_TABLE';
        };

        // 컬럼명을 안전하게 처리하는 함수 (ERD 컬럼 문법용)
        const safeColumnName = (name) => {
            if (!name || typeof name !== 'string') {
                return 'unknown_column';
            }

            // Mermaid에서 허용되지 않는 특수문자 제거
            // 한글은 유지하고 특수문자만 언더스코어로 변경
            let safeName = name.trim().replace(/[^a-zA-Z0-9가-힣_]/g, '_');

            // 숫자로 시작하면 c_를 앞에 추가
            if (/^\d/.test(safeName)) {
                safeName = 'c_' + safeName;
            }

            // 너무 긴 이름은 잘라내기
            if (safeName.length > 30) {
                safeName = safeName.substring(0, 27) + '___';
            }

            return safeName || 'unknown_column';
        };

        // 컬럼 설명 함수 (테스트용 - 설명 없이 순수 한글 컬럼명만 사용)
        const getColumnDescription = (name) => {
            // Mermaid 11.12.0 한글 지원 테스트를 위해 설명 비활성화
            return '';
        };

        // 표시 수준에 따라 컬럼을 줄인다 (렌더 비용의 지배 요인).
        const level = detailLevelRef.current || 'full';
        const applyDetail = (cols) => {
            if (!cols || level === 'full') return cols;
            if (level === 'none') return [];
            return cols.filter(c => c.isPrimaryKey || c.isForeignKey);   // keys
        };

        let mermaidCode = 'erDiagram\n';

        // 테이블 정의 (한글 테이블명/컬럼명을 안전하게 처리)
        tablesToShow.forEach((table, tableIndex) => {
            // (구) 여기서 표·컬럼마다 console.log 를 찍었다. 300표면 9천 회가 넘어
            //     렌더 때마다 콘솔이 마비되고 시간도 먹는다. 요약만 남긴다.

            // 테이블명 검증
            if (!table.tableName || !table.tableName.trim()) {
                console.warn('Empty table name found, skipping table');
                return;
            }

            const safeTableName = safeEntityName(table.tableName.trim());
            console.log(`Safe table name: ${table.tableName} -> ${safeTableName}`);

            // 안전한 테이블명인지 확인
            if (safeTableName === 'UNKNOWN_TABLE' || !safeTableName.trim()) {
                console.warn(`Skipping table with unsafe name: ${table.tableName}`);
                return;
            }

            try {
                mermaidCode += `    ${safeTableName} {\n`;
                let hasValidColumns = false;

            // 컬럼이 있는지 확인
            const shownColumns = applyDetail(table.columns);
            if (shownColumns && shownColumns.length > 0) {
                shownColumns.forEach(column => {
                    // 컬럼명과 데이터 타입 검증 (더 엄격한 검증)
                    if (column.columnName && column.columnName.trim() &&
                        column.dataType && column.dataType.trim()) {

                        const safeType = sanitizeDataType(column.dataType.trim());
                        const columnName = column.columnName.trim(); // 공백 제거

                        // 컬럼명 안전하게 처리 (특수문자 제거, 한글 유지)
                        const sanitizedColumnName = safeColumnName(columnName);

                        // 식별여부 표시 (PK/FK) - 데이터타입 뒤에 위치
                        const keyType = column.isPrimaryKey ? ' PK' : column.isForeignKey ? ' FK' : '';

                        // 유효한 컬럼만 추가 (타입과 컬럼명 모두 존재하고 빈 문자열이 아닌 경우)
                        if (safeType && safeType.length > 0 && sanitizedColumnName && sanitizedColumnName.length > 0) {
                            // 추가 검증: 컬럼명이 숫자로만 이루어져 있거나 Mermaid 키워드인지 확인
                            const reservedWords = ['entity', 'relationship', 'attribute', 'erDiagram'];
                            const isValidColumnName = !reservedWords.includes(sanitizedColumnName.toLowerCase()) &&
                                                    !/^\d+$/.test(sanitizedColumnName) &&
                                                    sanitizedColumnName.length <= 50; // 너무 긴 컬럼명 방지

                            if (isValidColumnName) {
                                // Mermaid ERD 올바른 형식: 타입 컬럼명 키타입
                                let columnLine;
                                if (keyType) {
                                    columnLine = `        ${safeType} ${sanitizedColumnName}${keyType}`;
                                } else {
                                    columnLine = `        ${safeType} ${sanitizedColumnName}`;
                                }

                                mermaidCode += columnLine + '\n';
                                hasValidColumns = true;

                            } else {
                                console.warn('Skipped invalid column name:', {
                                    originalName: column.columnName,
                                    sanitizedName: sanitizedColumnName,
                                    reason: 'Reserved word, numeric only, or too long'
                                });
                            }
                        } else {
                            console.warn('Skipped invalid column:', {
                                originalName: column.columnName,
                                sanitizedName: sanitizedColumnName,
                                dataType: column.dataType,
                                safeType: safeType
                            });
                        }
                    }
                });
            }

                // 유효한 컬럼이 하나도 없는 경우 더미 컬럼 추가
                if (!hasValidColumns) {
                    console.warn(`Table ${safeTableName} has no valid columns, adding dummy column`);
                    mermaidCode += `        string id PK\n`;
                    hasValidColumns = true;
                }

                mermaidCode += '    }\n\n';  // 테이블 블록 사이에 빈 줄 추가
            } catch (error) {
                console.error(`Error processing table ${safeTableName}:`, error);
                // 오류가 발생한 테이블은 건너뛰기
                console.warn(`Skipping problematic table: ${table.tableName}`);
            }
        });

        // 관계 정의 (선택된 테이블과 선택된 관계만 표시)
        if (erdData.relationships && erdData.relationships.length > 0) {
            mermaidCode += '\n';  // 관계 섹션 전에 빈 줄 추가

            // 선택된 테이블 이름 세트 생성
            const selectedTableNames = new Set(tablesToShow.map(table => table.tableName));

            // 선택된 관계 ID 세트 확인
            const selectedRelIds = filteredRelationshipIds || new Set();

            erdData.relationships.forEach((rel, index) => {
                const relationshipId = `rel_${index}`;

                // 관계 데이터 검증 및 선택된 테이블/관계인지 확인
                if (rel.parentTable && rel.parentTable.trim() &&
                    rel.childTable && rel.childTable.trim() &&
                    selectedTableNames.has(rel.parentTable) &&
                    selectedTableNames.has(rel.childTable) &&
                    (filteredRelationshipIds === null || selectedRelIds.has(relationshipId))) {

                    const cardinality = '||--o{'; // 기본 1:N 관계
                    const relationshipName = rel.relationshipName || 'references';
                    const safeParentTable = safeEntityName(rel.parentTable.trim());
                    const safeChildTable = safeEntityName(rel.childTable.trim());

                    // 안전한 관계명 처리
                    const safeRelationshipName = relationshipName.replace(/["']/g, '');

                    mermaidCode += `    ${safeParentTable} ${cardinality} ${safeChildTable} : "${safeRelationshipName}"\n`;
                }
            });

            /*
              추정 관계 — **점선**(mermaid ER 의 `..` = 비식별 관계)으로 그린다.
              선언된 FK 와 같은 실선으로 그리면 사람이 "이 DB 에는 FK 가 있다" 고 잘못 배운다.
              라벨에 판정(verified/probable/candidate)을 실어 얼마나 믿을지 보이게 한다.
            */
            if (showInferred) {
                for (const rel of inferred) {
                    const parent = rel.parent.table;
                    const child = rel.child.table;
                    if (!selectedTableNames.has(parent) || !selectedTableNames.has(child)) continue;
                    const cols = (rel.child.columns || []).join(',');
                    const label = `${rel.status}${cols ? ` ${cols}` : ''}`.replace(/["']/g, '');
                    mermaidCode += `    ${safeEntityName(parent)} ||..o{ ${safeEntityName(child)} : "${label}"
`;
                }
            }
        }

        console.log('Generated Mermaid ERD code (with Korean):', mermaidCode);

        // 생성된 코드 검증 (강화된 디버깅)
        const lines = mermaidCode.split('\n');
        console.log('Mermaid code line by line:');
        lines.forEach((line, index) => {
            console.log(`Line ${index + 1}: "${line}"`);

            // 잠재적 문제 검사
            if (line.trim() && !line.includes('erDiagram') && !line.includes('%%')) {
                // 특수문자나 문제가 될 수 있는 패턴 검사
                const problematicChars = line.match(/[^\w\s가-힣\{\}\|\-\:\"\_\[\]]/g);
                if (problematicChars) {
                    console.warn(`Potentially problematic characters in line ${index + 1}:`, problematicChars);
                }

                // 빈 컬럼명이나 타입 검사
                if (line.includes('undefined') || line.includes('null') || line.trim() === '') {
                    console.warn(`Empty or undefined content in line ${index + 1}`);
                }
            }
        });

        // 최종 검증: 기본 구조 확인
        if (!mermaidCode.includes('erDiagram')) {
            console.error('Missing erDiagram declaration');
            return `erDiagram\n    EMPTY["${t('common:erd.mermaid.syntaxError')}"]`;
        }

        return mermaidCode;
    };

    // 테이블 선택/해제 핸들러
    const handleTableToggle = (tableName) => {
        const newSelectedTables = new Set(selectedTables);
        if (newSelectedTables.has(tableName)) {
            newSelectedTables.delete(tableName);
        } else {
            newSelectedTables.add(tableName);
        }
        setSelectedTables(newSelectedTables);
    };

    // 관계 선택/해제 핸들러
    const handleRelationshipToggle = (relationshipId) => {
        const newSelectedRelationships = new Set(selectedRelationships);
        if (newSelectedRelationships.has(relationshipId)) {
            newSelectedRelationships.delete(relationshipId);
        } else {
            newSelectedRelationships.add(relationshipId);
        }
        setSelectedRelationships(newSelectedRelationships);
    };

    // 모든 관계 선택/해제 핸들러
    const handleSelectAllRelationships = () => {
        if (selectedRelationships.size === allRelationships.length) {
            setSelectedRelationships(new Set());
        } else {
            setSelectedRelationships(new Set(allRelationships.map(rel => rel.id)));
        }
    };

    // 드래그 앤 드롭 핸들러들
    const handleDragStart = (e, item, type) => {
        console.log('Drag start:', { item, type, target: e.target });

        setDraggedItem(item);
        setDraggedType(type);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', ''); // 필수: 일부 브라우저에서 드래그가 작동하도록 함

        // 드래그되는 실제 요소 찾기 (tableItem div)
        let dragElement = e.target;
        while (dragElement && !dragElement.classList.contains(styles.tableItem)) {
            dragElement = dragElement.parentElement;
        }

        if (dragElement) {
            dragElement.classList.add(styles.dragging);
            console.log('Added dragging class to:', dragElement);
        } else {
            console.warn('Could not find drag element');
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        // 드래그 오버 시 시각적 피드백 - 올바른 요소 찾기
        let dropElement = e.target;
        while (dropElement && !dropElement.classList.contains(styles.tableItem)) {
            dropElement = dropElement.parentElement;
        }

        if (dropElement) {
            dropElement.classList.add(styles.dragOver);
        }
    };

    const handleDragLeave = (e) => {
        // 드래그 리브 시 - 올바른 요소 찾기
        let dropElement = e.target;
        while (dropElement && !dropElement.classList.contains(styles.tableItem)) {
            dropElement = dropElement.parentElement;
        }

        if (dropElement) {
            dropElement.classList.remove(styles.dragOver);
        }
    };

    const handleDragEnd = (e) => {
        console.log('Drag end:', { target: e.target });

        // 모든 드래그 관련 스타일 제거 (전역으로)
        document.querySelectorAll(`.${styles.dragOver}`).forEach(el => {
            el.classList.remove(styles.dragOver);
            console.log('Removed dragOver from:', el);
        });
        document.querySelectorAll(`.${styles.dragging}`).forEach(el => {
            el.classList.remove(styles.dragging);
            console.log('Removed dragging from:', el);
        });

        // 드래그 상태 초기화
        setDraggedItem(null);
        setDraggedType(null);

        console.log('Drag ended, state cleaned up');
    };

    const handleDrop = (e, targetItem, targetType, targetIndex) => {
        e.preventDefault();
        console.log('Drop event:', { draggedItem, draggedType, targetItem, targetType, targetIndex });

        // 드래그 오버 스타일 제거 - 올바른 요소 찾기
        let dropElement = e.target;
        while (dropElement && !dropElement.classList.contains(styles.tableItem)) {
            dropElement = dropElement.parentElement;
        }
        if (dropElement) {
            dropElement.classList.remove(styles.dragOver);
        }

        // 유효하지 않은 드롭인 경우 그냥 리턴 (dragEnd에서 정리됨)
        if (!draggedItem || !draggedType || draggedType !== targetType || draggedItem === targetItem) {
            console.log('Invalid drop, ignoring:', {
                draggedItem,
                draggedType,
                targetType,
                sameItem: draggedItem === targetItem
            });
            return;
        }

        console.log('Valid drop detected:', { draggedItem, targetItem, targetType, targetIndex });

        if (targetType === 'table') {
            const newTables = [...allTables];
            const draggedIndex = newTables.indexOf(draggedItem);

            // targetIndex는 filteredTables 기준이므로 allTables 기준으로 변환
            const filteredTables = newTables.filter(tableName =>
                tableName.toLowerCase().includes(searchTerm.toLowerCase())
            );
            const targetTableName = filteredTables[targetIndex];
            const actualTargetIndex = newTables.indexOf(targetTableName);

            if (draggedIndex !== -1 && actualTargetIndex !== -1 && draggedIndex !== actualTargetIndex) {
                // 배열에서 요소 제거
                newTables.splice(draggedIndex, 1);
                // 새 위치에 삽입 (드래그된 아이템이 제거되었으므로 인덱스 조정)
                const insertIndex = draggedIndex < actualTargetIndex ? actualTargetIndex - 1 : actualTargetIndex;
                newTables.splice(insertIndex, 0, draggedItem);

                // 상태 업데이트로 즉시 리렌더링 발생 (useEffect가 감지하여 Mermaid 재생성됨)
                setAllTables(newTables);
                console.log('Table order updated (will trigger Mermaid regeneration):', {
                    draggedItem,
                    draggedIndex,
                    targetTableName,
                    actualTargetIndex,
                    insertIndex,
                    newOrder: newTables
                });
            }
        } else if (targetType === 'relationship') {
            const newRelationships = [...allRelationships];
            const draggedIndex = newRelationships.findIndex(rel => rel.id === draggedItem);

            if (draggedIndex !== -1 && draggedIndex !== targetIndex) {
                // 배열에서 요소 제거
                const [draggedRelationship] = newRelationships.splice(draggedIndex, 1);
                // 새 위치에 삽입
                newRelationships.splice(targetIndex, 0, draggedRelationship);

                // 상태 업데이트로 즉시 리렌더링 발생 (useEffect가 감지하여 Mermaid 재생성됨)
                setAllRelationships(newRelationships);
                console.log('Relationship order updated (will trigger Mermaid regeneration):', newRelationships);
            }
        }

        // 상태 초기화는 handleDragEnd에서 처리됨
    };

    /**
     * **이 테이블 주변만 보기** — 관계선을 따라 N홉 이웃을 고른다.
     *
     * 큰 스키마에서 ERD 가 못 쓰게 되는 이유는 표가 많아서다. 지금까지의 대응은 **컬럼을
     * 숨기는 것**이었는데(자동 상세도), 그러면 표 300개가 여전히 300개고 정보만 빠진다.
     * 줄여야 하는 축은 컬럼이 아니라 **표**다 — 사람이 한 번에 읽는 것은 "이 테이블과
     * 그 주변" 이지 스키마 전체가 아니다.
     *
     * 방향은 따지지 않는다(부모·자식 양쪽). 조인 경로를 보려는 것이라 화살표 방향으로
     * 끊으면 정작 알고 싶은 이웃이 빠진다.
     */
    const neighborhoodOf = (center, hops) => {
        const adj = new Map();
        const link = (a, b) => {
            if (!adj.has(a)) adj.set(a, new Set());
            adj.get(a).add(b);
        };
        for (const rel of allRelationships) {
            if (!rel || !rel.parentTable || !rel.childTable) continue;
            link(rel.parentTable, rel.childTable);
            link(rel.childTable, rel.parentTable);
        }
        const seen = new Set([center]);
        let frontier = [center];
        for (let h = 0; h < hops; h += 1) {
            const next = [];
            for (const t of frontier) {
                for (const n of (adj.get(t) || [])) {
                    if (!seen.has(n)) { seen.add(n); next.push(n); }
                }
            }
            frontier = next;
            if (!frontier.length) break;
        }
        return seen;
    };

    /**
     * **추정 관계** — FK 로 선언되지 않았지만 근거가 있는 관계.
     *
     * 이것이 없으면 FK 를 안 건 스키마의 ERD 는 상자만 뜨고 선이 하나도 없다(교육DB 실측:
     * FK 0건). 추정 엔진(`assistant/relationship/`)이 키·인덱스·도메인·운영SQL 을 근거로
     * 찾아 둔 것을 읽어 **점선**으로 함께 그린다.
     *
     * 기본은 켜 두되 **점선과 범례로 추정임을 분명히** 한다 — 선언된 FK 와 같은 실선으로
     * 그리면 사람이 "이 DB 에는 FK 가 있다" 고 잘못 배운다.
     */
    const [inferred, setInferred] = useState([]);
    const [showInferred, setShowInferred] = useState(true);
    const [inferring, setInferring] = useState(false);

    /** 중심 테이블 — 비어 있으면 「주변만 보기」가 무엇을 중심으로 할지 모른다. */
    const [hopCenter, setHopCenter] = useState('');
    const [hopDepth, setHopDepth] = useState(1);

    const applyNeighborhood = () => {
        if (!hopCenter) return;
        const near = neighborhoodOf(hopCenter, hopDepth);
        setSelectedTables(new Set([...near].filter(t => allTables.includes(t))));
        // 표가 줄었으니 컬럼은 다시 다 보여 준다 — 줄이는 축을 바꾼 것이 이 기능의 요점이다
        detailLevelRef.current = 'full';
        setDetailLevel('full');
        toast.info(t('common:erd.hopApplied', { center: hopCenter, hops: hopDepth, count: near.size }));
    };

    /** 저장된 추정 관계를 읽어 이 스키마의 것만 남긴다. 없으면 조용히 빈 목록이다. */
    const loadInferred = async (schema) => {
        const connId = connectionData?.id;
        if (!connId || !schema) return;
        try {
            const r = await fetchRelationships({ connectionId: connId, limit: 500 });
            const up = String(schema).toUpperCase();
            setInferred((r.relationships || []).filter(x =>
                String(x.child.schema).toUpperCase() === up
                && String(x.parent.schema).toUpperCase() === up
                // 선언된 것(=FK)은 이미 실선으로 그려진다 — 겹쳐 그리면 선이 두 겹이 된다
                && x.status !== 'declared'));
        } catch (_e) {
            // 읽지 못하는 것은 **없는 것과 같다** — ERD 본체를 막지 않는다
            setInferred([]);
        }
    };

    /** 지금 추정을 돌린다. DB 를 읽으므로 시간이 걸린다 — 버튼이 진행을 보여 준다. */
    const runInference = async () => {
        const connId = connectionData?.id;
        if (!connId || !selectedSchema) return;
        setInferring(true);
        try {
            const r = await analyzeRelationships({ connectionId: connId, schema: selectedSchema });
            await loadInferred(selectedSchema);
            toast.success(t('common:erd.inferDone', { n: (r && r.stored) || 0 }));
            setShowInferred(true);
        } catch (err) {
            toast.error(t('common:erd.inferFailed', { message: err.message }));
        } finally {
            setInferring(false);
        }
    };

    // 모든 테이블 선택/해제
    const handleSelectAll = () => {
        if (selectedTables.size === filteredTables.length) {
            setSelectedTables(new Set()); // 모두 해제
        } else {
            setSelectedTables(new Set(filteredTables)); // 모두 선택
        }
    };

    // 검색어로 필터링된 테이블 목록
    const filteredTables = allTables.filter(tableName =>
        tableName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // 선택된 테이블/관계가 변경되거나 순서가 바뀌면 다이어그램 다시 생성
    useEffect(() => {
        if (originalErdData && selectedTables.size >= 0) {
            console.log(`[ERD] regenerate: tables=${selectedTables.size}/${allTables.length}, rels=${selectedRelationships.size}, detail=${detailLevel}`);

            // 테이블과 관계 순서를 반영하여 ERD 데이터 재구성
            const reorderedErdData = {
                ...originalErdData,
                tables: allTables.map(tableName =>
                    originalErdData.tables.find(table => table.tableName === tableName)
                ).filter(Boolean),
                relationships: allRelationships.map(orderedRel => {
                    const relIndex = parseInt(orderedRel.id.split('_')[1]);
                    return originalErdData.relationships[relIndex];
                }).filter(Boolean)
            };

            const mermaidCode = generateMermaidERD(reorderedErdData, selectedTables, selectedRelationships);
            setErdDiagram(mermaidCode);

            // 다이어그램 렌더링
            setTimeout(() => {
                if (diagramRef.current) {
                    renderDiagramWithCode(mermaidCode);
                }
            }, 100);
        }
    }, [selectedTables, selectedRelationships, allTables, allRelationships, originalErdData, detailLevel,
        inferred, showInferred]);

    const renderDiagramWithCode = async (mermaidCode) => {
        if (!diagramRef.current) {
            console.error('diagramRef.current is null');
            return;
        }

        console.log('Starting diagram rendering with code...');
        console.log('Mermaid code:', mermaidCode);

        try {
            // 기존 다이어그램과 에러 요소 완전 제거
            diagramRef.current.innerHTML = '';

            // Mermaid 완전 재초기화
            if (typeof mermaid.reset === 'function') {
                mermaid.reset();
            }

            // Mermaid 재설정 - 레이아웃 엔진 설정 추가
            mermaid.initialize(mermaidConfig(isDarkTheme()));

            // 고유한 ID 생성 (재렌더링 시 충돌 방지)
            const uniqueId = `erd-diagram-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            // 새로운 다이어그램 렌더링
            console.log('Calling mermaid.render with unique ID:', uniqueId);
            console.log('Mermaid code being rendered:', mermaidCode);
            const { svg } = await mermaid.render(uniqueId, mermaidCode);
            console.log('Mermaid render successful, SVG length:', svg?.length);

            // DOM 업데이트를 확실히 하기 위해 requestAnimationFrame 사용
            requestAnimationFrame(() => {
                if (diagramRef.current) {
                    diagramRef.current.innerHTML = svg;

                    // SVG 스타일 조정 및 상호작용 추가
                    const svgElement = diagramRef.current.querySelector('svg');
                    if (svgElement) {
                        applySVGStyling(svgElement);
                        addInteractivityToSVG(svgElement);
                        console.log('SVG element styled and made interactive successfully');
                    } else {
                        console.error('SVG element not found after rendering');
                    }
                }
            });
        } catch (err) {
            console.error('Mermaid 렌더링 오류:', err);
            console.error('Error details:', {
                message: err.message,
                stack: err.stack,
                name: err.name
            });
            setError(t('common:erd.err.diagram', { message: err.message }));
        }
    };

    const renderDiagram = async () => {
        if (!diagramRef.current) {
            console.error('diagramRef.current is null');
            return;
        }

        // DOM 요소가 실제로 Document에 연결되어 있는지 확인
        if (!document.contains(diagramRef.current)) {
            console.error('diagramRef.current is not connected to DOM, retrying...');
            // DOM이 준비될 때까지 재시도
            setTimeout(() => renderDiagram(), 100);
            return;
        }

        console.log('Starting diagram rendering...');
        console.log('ERD diagram data:', erdDiagram);

        try {
            // 기존 다이어그램 제거
            diagramRef.current.innerHTML = '';

            // Mermaid 캐시 클리어 (중요!)
            if (typeof mermaid.reset === 'function') {
                mermaid.reset();
            }

            // 고유한 ID 생성 (재렌더링 시 충돌 방지)
            const uniqueId = `erd-diagram-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

            // 새로운 다이어그램 렌더링
            console.log('Calling mermaid.render with unique ID:', uniqueId);
            console.log('ERD diagram code being rendered:', erdDiagram);

            // Mermaid 렌더링 시 더 안전한 에러 핸들링
            let svg;
            try {
                const result = await mermaid.render(uniqueId, erdDiagram);
                svg = result.svg;
                console.log('Mermaid render successful, SVG length:', svg?.length);
            } catch (renderError) {
                console.error('Mermaid render failed:', renderError);

                // 렌더링 실패 시 생성된 임시 요소 제거
                const failedElement = document.getElementById(uniqueId);
                if (failedElement && failedElement.parentNode) {
                    failedElement.parentNode.removeChild(failedElement);
                }

                throw new Error(t('common:erd.err.render', { message: renderError.message }));
            }

            // DOM 업데이트를 이중으로 확실히 하기 위해 setTimeout과 requestAnimationFrame 사용
            await new Promise(resolve => setTimeout(resolve, 0));

            requestAnimationFrame(() => {
                if (diagramRef.current) {
                    diagramRef.current.innerHTML = svg;

                    // SVG 적용 후 다시 한번 확인
                    requestAnimationFrame(() => {
                        // SVG 스타일 조정 및 상호작용 추가
                        const svgElement = diagramRef.current?.querySelector('svg');
                        if (svgElement) {
                            applySVGStyling(svgElement);
                            addInteractivityToSVG(svgElement);
                            console.log('SVG element styled and made interactive successfully');
                        } else {
                            console.error('SVG element not found after rendering');
                        }
                    });
                }
            });
        } catch (err) {
            console.error('Mermaid 렌더링 오류:', err);
            console.error('Error details:', {
                message: err.message,
                stack: err.stack,
                name: err.name
            });
            setError(t('common:erd.err.diagram', { message: err.message }));
        }
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    const handleZoomIn = () => {
        setAutoFit(false);
        setZoomLevel(prev => Math.min(prev + 25, 500));
    };

    const handleZoomOut = () => {
        setAutoFit(false);
        setZoomLevel(prev => Math.max(prev - 25, 25));
    };

    const handleAutoFit = () => {
        setAutoFit(true);
        setZoomLevel(100);
    };

    // 소스 코드 보기 토글
    const toggleSourceCode = () => {
        const newShowSourceCode = !showSourceCode;
        setShowSourceCode(newShowSourceCode);

        // 소스코드에서 다이어그램으로 전환할 때 다이어그램 다시 렌더링
        if (!newShowSourceCode && erdDiagram) {
            setTimeout(() => {
                console.log('Re-rendering diagram after source code toggle');
                renderDiagramWithCode(erdDiagram);
            }, 100);
        }
    };

    const handleZoomChange = (e) => {
        const value = e.target.value;
        if (value === 'auto') {
            setAutoFit(true);
            setZoomLevel(100);
        } else {
            setAutoFit(false);
            setZoomLevel(parseInt(value));
        }
    };

    /**
     * SVG 로 저장 — **벡터**라 확대해도 안 깨지고, 문서·인쇄에 그대로 들어간다.
     *
     * PNG 는 화면 크기에 묶인 래스터라 큰 ERD 를 인쇄하면 글자가 뭉갠다. mermaid 가 이미
     * SVG 를 그려 놨으므로 캔버스를 거칠 이유가 없다 — 그대로 내려받는다.
     *
     * 배경을 칠해 두는 이유: 다크 테마에서 그린 SVG 는 글자가 밝은데 배경이 투명이라,
     * 흰 문서에 넣으면 **아무것도 안 보인다.**
     */
    const handleSaveSvg = () => {
        const svgElement = diagramRef.current?.querySelector('svg');
        if (!svgElement) return;
        try {
            const clone = svgElement.cloneNode(true);
            const bbox = svgElement.getBBox();
            clone.setAttribute('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
            clone.setAttribute('width', bbox.width);
            clone.setAttribute('height', bbox.height);
            clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
            clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
            const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            bg.setAttribute('x', bbox.x); bg.setAttribute('y', bbox.y);
            bg.setAttribute('width', bbox.width); bg.setAttribute('height', bbox.height);
            bg.setAttribute('fill', dark ? '#1e1e1e' : '#ffffff');
            clone.insertBefore(bg, clone.firstChild);

            const data = new XMLSerializer().serializeToString(clone);
            const url = URL.createObjectURL(new Blob([data], { type: 'image/svg+xml;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `${selectedSchema}_ERD_${new Date().toISOString().slice(0, 10)}.svg`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('SVG 저장 실패:', err);
            toast.error(t('common:erd.saveSvgFailed'));
        }
    };

    // 이미지 저장 기능
    const handleSaveImage = () => {
        const svgElement = diagramRef.current?.querySelector('svg');
        if (!svgElement) {
            console.error('SVG 요소를 찾을 수 없습니다.');
            return;
        }

        try {
            // SVG 복사본 생성 및 스타일 인라인화
            const svgClone = svgElement.cloneNode(true);

            // SVG 크기 설정
            const bbox = svgElement.getBBox();
            svgClone.setAttribute('viewBox', `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`);
            svgClone.setAttribute('width', bbox.width);
            svgClone.setAttribute('height', bbox.height);

            // 네임스페이스 설정
            svgClone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
            svgClone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

            // SVG를 문자열로 변환
            const svgData = new XMLSerializer().serializeToString(svgClone);

            // Canvas 생성 및 변환
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = function() {
                // 고해상도를 위해 크기 조정
                const scale = 2;
                canvas.width = bbox.width * scale;
                canvas.height = bbox.height * scale;

                // 고해상도 렌더링 설정
                ctx.scale(scale, scale);

                // 배경을 흰색으로 설정
                ctx.fillStyle = 'white';
                ctx.fillRect(0, 0, bbox.width, bbox.height);

                // SVG 이미지 그리기
                ctx.drawImage(img, 0, 0, bbox.width, bbox.height);

                // PNG로 다운로드
                canvas.toBlob(function(blob) {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedSchema}_ERD_${new Date().toISOString().slice(0, 10)}.png`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                }, 'image/png');
            };

            // SVG를 Data URL로 변환
            const encodedData = encodeURIComponent(svgData);
            img.src = 'data:image/svg+xml;charset=utf-8,' + encodedData;

        } catch (error) {
            console.error('이미지 저장 중 오류가 발생했습니다:', error);
        }
    };

    const applySVGStyling = (svgElement) => {
        if (autoFit) {
            // Auto-fit: 컨테이너에 맞춤
            svgElement.style.width = '100%';
            svgElement.style.height = 'auto';
            svgElement.style.maxWidth = '100%';
            svgElement.style.transform = 'none';
            svgElement.style.transformOrigin = '';
            // autoFit 모드에서는 패닝 상태 초기화
            currentTranslateXRef.current = 0;
            currentTranslateYRef.current = 0;
            addDebugLog('applySVGStyling: autoFit mode', { transform: 'none' });
        } else {
            // Manual zoom: 실제 크기 기준으로 확대/축소
            svgElement.style.width = 'auto';
            svgElement.style.height = 'auto';
            svgElement.style.maxWidth = 'none';

            // scale 먼저, translate 나중에 (SVG 좌표계에서 이동)
            const translateX = currentTranslateXRef.current;
            const translateY = currentTranslateYRef.current;
            const transform = `scale(${zoomLevel / 100}) translate(${translateX}px, ${translateY}px)`;
            svgElement.style.transform = transform;
            svgElement.style.transformOrigin = '0 0';

            addDebugLog('applySVGStyling: manual mode', {
                zoomLevel,
                translate: `${translateX.toFixed(0)},${translateY.toFixed(0)}`,
                transform
            });
        }
    };

    // 관계 포커스 기능 - 관련된 두 테이블을 모두 포함하는 영역으로 포커스
    const handleRelationshipFocus = (relationship) => {
        addDebugLog('RELATIONSHIP FOCUS START', {
            id: relationship.id,
            parent: relationship.parentTable,
            child: relationship.childTable,
            display: relationship.displayName
        });

        if (!diagramRef.current) {
            addDebugLog('ERROR: diagramRef is null');
            return;
        }

        const svgElement = diagramRef.current.querySelector('svg');
        if (!svgElement) {
            addDebugLog('ERROR: SVG element not found');
            return;
        }

        // 두 테이블 요소 찾기 (정확한 매칭 우선) - 테이블 포커스와 동일한 로직 사용
        const findTableElements = (tableName) => {
            const allTextElements = svgElement.querySelectorAll('text, tspan, foreignObject');
            const safeName = tableName.replace(/[^a-zA-Z0-9가-힣_]/g, '_');

            let textElement = null;

            // 1차: 정확한 매칭
            for (const textEl of allTextElements) {
                const content = textEl.textContent?.trim();
                if (content === tableName || content === safeName) {
                    addDebugLog(`Found exact match for ${tableName}`, { content });
                    textElement = textEl;
                    break;
                }
            }

            // 2차: 부분 매칭 (정확한 단어 경계)
            if (!textElement) {
                for (const textEl of allTextElements) {
                    const content = textEl.textContent?.trim();
                    if (content && (content.startsWith(tableName) || content.startsWith(safeName))) {
                        addDebugLog(`Found partial match for ${tableName}`, { content });
                        textElement = textEl;
                        break;
                    }
                }
            }

            if (!textElement) {
                addDebugLog(`ERROR: No match found for ${tableName}`);
                return null;
            }

            // 텍스트를 포함하는 가장 큰 그룹 찾기 (테이블 전체)
            let targetGroup = textElement.closest('g');
            if (!targetGroup) {
                return null;
            }

            // 상위 그룹들을 확인하여 더 큰 그룹이 있으면 사용
            let parent = targetGroup.parentElement;
            while (parent && parent.tagName === 'g') {
                try {
                    const parentBBox = parent.getBBox();
                    const currentBBox = targetGroup.getBBox();

                    // 부모가 현재보다 크지만 전체 SVG보다는 작으면 사용
                    if (parentBBox.width > currentBBox.width &&
                        parentBBox.height > currentBBox.height &&
                        parentBBox.width < svgElement.getBBox().width * 0.9) {
                        targetGroup = parent;
                        parent = parent.parentElement;
                    } else {
                        break;
                    }
                } catch (e) {
                    break;
                }
            }

            return targetGroup;
        };

        const parentTableGroup = findTableElements(relationship.parentTable);
        const childTableGroup = findTableElements(relationship.childTable);

        if (!parentTableGroup || !childTableGroup) {
            addDebugLog('ERROR: Could not find both table groups');
            return;
        }

        try {
            // 현재 줌 레벨 가져오기
            const currentZoomLevel = zoomLevelRef.current || 100;
            const currentScale = currentZoomLevel / 100;

            // autoFit 모드 해제
            setAutoFit(false);

            // 1. 현재 화면상 두 테이블의 위치 얻기
            const parentRect = parentTableGroup.getBoundingClientRect();
            const childRect = childTableGroup.getBoundingClientRect();
            const containerRect = diagramRef.current.getBoundingClientRect();

            // 두 테이블을 포함하는 전체 영역 계산 (화면 좌표)
            const minLeft = Math.min(parentRect.left, childRect.left);
            const minTop = Math.min(parentRect.top, childRect.top);
            const maxRight = Math.max(parentRect.right, childRect.right);
            const maxBottom = Math.max(parentRect.bottom, childRect.bottom);

            // 두 테이블 영역의 중심점 (화면 좌표)
            const combinedScreenCenterX = (minLeft + maxRight) / 2;
            const combinedScreenCenterY = (minTop + maxBottom) / 2;

            // 컨테이너의 중심점 (화면 좌표)
            const containerScreenCenterX = containerRect.left + containerRect.width / 2;
            const containerScreenCenterY = containerRect.top + containerRect.height / 2;

            // 화면상에서 이동해야 할 거리 (픽셀)
            // 화면 px → CSS px(translate 단위, 웹의 화면 확대 utils/appZoom)
            const screenDeltaX = toCssPx(containerScreenCenterX - combinedScreenCenterX);
            const screenDeltaY = toCssPx(containerScreenCenterY - combinedScreenCenterY);

            // 2. 현재 translate 값에 화면 델타를 더함
            const oldTranslateX = currentTranslateXRef.current;
            const oldTranslateY = currentTranslateYRef.current;
            const translateX = oldTranslateX + (screenDeltaX / currentScale);
            const translateY = oldTranslateY + (screenDeltaY / currentScale);

            // 전역 패닝 상태 업데이트
            currentTranslateXRef.current = translateX;
            currentTranslateYRef.current = translateY;

            // transform 적용 (현재 줌 레벨 유지)
            const newTransform = `scale(${currentScale}) translate(${translateX}px, ${translateY}px)`;
            svgElement.style.transition = 'transform 0.3s ease-out';
            svgElement.style.transform = newTransform;

            addDebugLog('Relationship transform applied');

            // 두 테이블 사이의 관계선(path/line) 찾아서 강조
            const allPathElements = svgElement.querySelectorAll('path, line');
            let relationshipLineFound = false;

            // 먼저 모든 path 강조 효과 제거
            allPathElements.forEach(path => {
                path.style.stroke = '';
                path.style.strokeWidth = '';
                path.style.filter = '';
            });

            // 부모/자식 테이블의 중심점 계산 (화면 좌표)
            const parentCenterX = parentRect.left + parentRect.width / 2;
            const parentCenterY = parentRect.top + parentRect.height / 2;
            const childCenterX = childRect.left + childRect.width / 2;
            const childCenterY = childRect.top + childRect.height / 2;

            // 두 테이블을 연결하는 선 찾기 (가장 가까운 path 찾기)
            let closestPath = null;
            let minDistance = Infinity;

            allPathElements.forEach(pathElement => {
                try {
                    const pathRect = pathElement.getBoundingClientRect();

                    // path의 중심점
                    const pathCenterX = pathRect.left + pathRect.width / 2;
                    const pathCenterY = pathRect.top + pathRect.height / 2;

                    // 두 테이블 중심의 중간점
                    const midX = (parentCenterX + childCenterX) / 2;
                    const midY = (parentCenterY + childCenterY) / 2;

                    // path 중심과 두 테이블 중간점 사이의 거리
                    const distance = Math.sqrt(
                        Math.pow(pathCenterX - midX, 2) +
                        Math.pow(pathCenterY - midY, 2)
                    );

                    // path가 두 테이블 사이 영역에 있는지 확인
                    const isInBetween =
                        pathRect.left >= Math.min(parentRect.left, childRect.left) - 50 &&
                        pathRect.right <= Math.max(parentRect.right, childRect.right) + 50 &&
                        pathRect.top >= Math.min(parentRect.top, childRect.top) - 50 &&
                        pathRect.bottom <= Math.max(parentRect.bottom, childRect.bottom) + 50;

                    if (isInBetween && distance < minDistance) {
                        minDistance = distance;
                        closestPath = pathElement;
                        relationshipLineFound = true;
                    }
                } catch (e) {
                    // getBoundingClientRect 실패 무시
                }
            });

            // 가장 가까운 path만 강조 (작게)
            if (closestPath) {
                closestPath.style.stroke = '#ff0000';
                closestPath.style.strokeWidth = '2';
                closestPath.style.filter = 'drop-shadow(0 0 3px rgba(255, 0, 0, 0.6))';
            }

            addDebugLog('Relationship line highlight', {
                found: relationshipLineFound,
                totalPaths: allPathElements.length,
                minDistance: minDistance.toFixed(0)
            });

            // 효과 제거 타이머
            setTimeout(() => {
                svgElement.style.transition = '';
                allPathElements.forEach(path => {
                    path.style.stroke = '';
                    path.style.strokeWidth = '';
                    path.style.filter = '';
                });
                addDebugLog('Highlight removed after 2s');
            }, 2000);

            addDebugLog('Relationship focus completed');

        } catch (error) {
            console.error('Error in relationship focus:', error);
        }
    };

    // SVG 내 테이블 요소에 클릭 이벤트 바인딩
    const addTableClickEvents = (svgElement) => {
        console.log('Adding table click events to SVG');

        // 모든 텍스트 요소를 찾아서 테이블명인지 확인
        const allTextElements = svgElement.querySelectorAll('text, tspan, foreignObject');
        console.log('Found text elements:', allTextElements.length);

        // 현재 테이블 목록 가져오기
        const currentTables = allTables || [];
        console.log('Current tables:', currentTables);

        allTextElements.forEach((textElement) => {
            const textContent = textElement.textContent?.trim();
            if (!textContent) return;

            // 현재 텍스트가 테이블명인지 확인
            const matchingTable = currentTables.find(tableName => {
                // 정확한 매치 또는 safeEntityName 변환 후 매치
                const safeName = tableName.replace(/[^a-zA-Z0-9가-힣_]/g, '_');
                return textContent === tableName || textContent === safeName || textContent.includes(tableName);
            });

            if (matchingTable) {
                console.log('Adding click event to table:', matchingTable, 'text:', textContent);

                // 테이블 그룹 찾기
                let tableGroup = textElement.closest('g');
                if (tableGroup) {
                    // 클릭 이벤트 추가
                    tableGroup.style.cursor = 'pointer';
                    tableGroup.addEventListener('click', (e) => {
                        e.stopPropagation(); // 패닝 이벤트 방지
                        console.log('Table clicked in SVG:', matchingTable);

                        // 즉시 포커스 (지연 없이)
                        handleTableFocus(matchingTable);
                    });

                    // 호버 효과도 추가
                    tableGroup.addEventListener('mouseenter', () => {
                        tableGroup.style.opacity = '0.8';
                    });

                    tableGroup.addEventListener('mouseleave', () => {
                        tableGroup.style.opacity = '1';
                    });
                }
            }
        });

        console.log('Table click events added successfully');
    };

    // 패닝 및 휠 줌 기능 추가
    const addInteractivityToSVG = (svgElement) => {
        // 1. 먼저 ERD 테이블에 클릭 이벤트 바인딩
        addTableClickEvents(svgElement);

        // 2. 전체 화면 패닝 기능
        let isPanning = false;
        let panStartX = 0;
        let panStartY = 0;

        const diagramContainer = diagramRef.current;
        if (diagramContainer) {
            diagramContainer.addEventListener('mousedown', (e) => {
                isPanning = true;
                panStartX = e.clientX;
                panStartY = e.clientY;
                diagramContainer.style.cursor = 'grabbing';
            });

            const handlePanMove = (e) => {
                if (!isPanning) return;

                e.preventDefault();
                // 화면 확대(utils/appZoom) 중이면 끈 거리(화면 px)를 CSS px 로
                const deltaX = toCssPx(e.clientX - panStartX);
                const deltaY = toCssPx(e.clientY - panStartY);

                // 줌 레벨을 고려한 실제 이동량 계산
                // scale이 먼저 적용되므로 translate는 scaled space에서 계산되어야 함
                const zoomScale = zoomLevelRef.current / 100;

                // 화면 픽셀 이동량을 SVG 좌표계 이동량으로 변환
                const svgDeltaX = deltaX / zoomScale;
                const svgDeltaY = deltaY / zoomScale;

                currentTranslateXRef.current += svgDeltaX;
                currentTranslateYRef.current += svgDeltaY;

                svgElement.style.transformOrigin = '0 0';   // 휠 확대와 같은 기준으로 이동
                svgElement.style.transform = `scale(${zoomScale}) translate(${currentTranslateXRef.current}px, ${currentTranslateYRef.current}px)`;

                panStartX = e.clientX;
                panStartY = e.clientY;
            };

            const handlePanEnd = () => {
                if (!isPanning) return;

                isPanning = false;
                diagramContainer.style.cursor = 'grab';
            };

            // 마우스 휠을 이용한 확대/축소 기능
            const handleWheel = (e) => {
                e.preventDefault();

                // 아래 좌표 계산은 transform-origin 이 0 0 일 때만 성립한다.
                // autoFit 모드는 origin 을 기본값(50% 50%)으로 두므로, 여기서 먼저 고정하지
                // 않으면 중앙 기준으로 확대되어 확대 시 위로 / 축소 시 아래로 밀린다.
                svgElement.style.transformOrigin = '0 0';

                // 현재 줌 레벨 가져오기
                const currentZoom = zoomLevelRef.current;

                // 휠 방향에 따라 줌 레벨 조정
                const delta = e.deltaY > 0 ? -10 : 10; // 휠 아래로: 축소, 휠 위로: 확대
                const newZoomLevel = Math.max(25, Math.min(500, currentZoom + delta));

                // auto-fit 모드 해제
                setAutoFit(false);
                setZoomLevel(newZoomLevel);

                // 현재 스케일에서의 SVG 화면 위치
                const svgRect = svgElement.getBoundingClientRect();
                const currentScale = currentZoom / 100;
                const newScale = newZoomLevel / 100;

                // 화면 좌표(마우스·rect)를 CSS px 로 — translate 는 CSS px 이다(화면 확대 중이면 둘이 다르다, utils/appZoom)
                const mx = toCssPx(e.clientX); const my = toCssPx(e.clientY);
                const rl = toCssPx(svgRect.left); const rt = toCssPx(svgRect.top);

                // 레이아웃상의 원래 좌상단 좌표 (transform 적용 전)
                const layoutLeft = rl - (currentTranslateXRef.current * currentScale);
                const layoutTop = rt - (currentTranslateYRef.current * currentScale);

                // 마우스가 가리키는 SVG 내부 좌표
                const mouseSvgX = (mx - rl) / currentScale;
                const mouseSvgY = (my - rt) / currentScale;

                // 새 스케일에서 마우스 위치가 유지되도록 새로운 Translate 계산
                const newTranslateX = (mx - layoutLeft) / newScale - mouseSvgX;
                const newTranslateY = (my - layoutTop) / newScale - mouseSvgY;

                // 전역 변수 업데이트
                currentTranslateXRef.current = newTranslateX;
                currentTranslateYRef.current = newTranslateY;

                // SVG 트랜스폼 업데이트
                svgElement.style.transform = `scale(${newScale}) translate(${currentTranslateXRef.current}px, ${currentTranslateYRef.current}px)`;
            };

            // 다이어그램은 필터 변경·스키마 전환마다 다시 그려진다. 정리 없이 붙이면
            // 같은 요소에 핸들러가 계속 쌓이고, document 리스너는 모달을 닫아도 남는다.
            if (interactionCleanupRef.current) interactionCleanupRef.current();
            document.addEventListener('mousemove', handlePanMove);
            document.addEventListener('mouseup', handlePanEnd);
            diagramContainer.addEventListener('wheel', handleWheel, { passive: false });
            interactionCleanupRef.current = () => {
                document.removeEventListener('mousemove', handlePanMove);
                document.removeEventListener('mouseup', handlePanEnd);
                diagramContainer.removeEventListener('wheel', handleWheel);
                interactionCleanupRef.current = null;
            };
        }
    };

    // 모달이 사라질 때 남은 document 리스너를 반드시 떼어낸다.
    useEffect(() => () => { if (interactionCleanupRef.current) interactionCleanupRef.current(); }, []);

    useEffect(() => { detailLevelRef.current = detailLevel; }, [detailLevel]);

    // zoom level과 autoFit 값을 ref에 동기화
    useEffect(() => {
        zoomLevelRef.current = zoomLevel;
    }, [zoomLevel]);

    useEffect(() => {
        autoFitRef.current = autoFit;
    }, [autoFit]);

    // zoom level이 변경될 때 기존 SVG에 스타일 적용
    useEffect(() => {
        if (diagramRef.current) {
            const svgElement = diagramRef.current.querySelector('svg');
            if (svgElement) {
                addDebugLog('useEffect: zoom/autoFit changed', {
                    zoomLevel,
                    autoFit,
                    translateX: currentTranslateXRef.current.toFixed(0),
                    translateY: currentTranslateYRef.current.toFixed(0)
                });
                requestAnimationFrame(() => {
                    applySVGStyling(svgElement);
                });
            }
        }
    }, [zoomLevel, autoFit]);

    // 모달이 열릴 때마다 ERD가 제대로 표시되도록 보장
    useEffect(() => {
        if (isOpen && erdDiagram) {
            // DOM 요소가 완전히 준비될 때까지 대기하는 재귀 함수
            const waitForDOMAndRender = (attempts = 0) => {
                if (attempts > 20) {
                    console.warn('Max attempts reached for DOM readiness check');
                    return;
                }

                if (!diagramRef.current) {
                    // DOM 요소가 아직 준비되지 않음, 다시 시도
                    setTimeout(() => waitForDOMAndRender(attempts + 1), 50);
                    return;
                }

                // 모달이 완전히 열린 후 다이어그램 재렌더링
                setTimeout(() => {
                    if (diagramRef.current) {
                        const svgElement = diagramRef.current.querySelector('svg');
                        if (!svgElement && erdDiagram) {
                            console.log('Modal opened but SVG not found, re-rendering...');
                            renderDiagram();
                        }
                    }
                }, 150);
            };

            // 초기 지연 후 DOM 체크 시작
            const timeoutId = setTimeout(() => waitForDOMAndRender(), 100);
            return () => clearTimeout(timeoutId);
        }
    }, [isOpen, erdDiagram]);

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            // Modal 닫을 때 Mermaid 에러 요소들 정리
            console.log('ERD Modal overlay clicked, cleaning up mermaid elements');
            const allMermaidElements = document.querySelectorAll('[id*="mermaid"], [id*="erd-diagram"]');
            allMermaidElements.forEach(element => {
                if (!element.closest('.modalOverlay')) {
                    console.log('Removing mermaid element on overlay click:', element.id || element.className);
                    element.remove();
                }
            });
            onClose();
        }
    };

    if (!isOpen) return null;

    const modalContent = (
        <div
            className={`${styles.modalOverlay} ${isFullscreen ? styles.fullscreen : ''}`}
            onClick={handleOverlayClick}
        >
            <div className={`${styles.modalContent} ${isFullscreen ? styles.fullscreenContent : ''}`}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        {selectedSchema} {t('common:erd.schemaErd')}
                    </h2>

                    {/* Debug Log Panel (주석처리) */}
                    {/* {debugLogs.length > 0 && (
                        <div style={{...}}>
                            디버그 로그 패널
                        </div>
                    )} */}

                    {/* Zoom Controls */}
                    <div className={styles.zoomControls}>
                        <button
                            className={styles.controlButton}
                            onClick={handleZoomOut}
                            title={t('common:erd.zoomOut')}
                            disabled={zoomLevel <= 25}
                        >
                            <FaSearchMinus />
                        </button>

                        <select
                            className={styles.zoomSelect}
                            value={detailLevel}
                            onChange={(e) => setDetailLevel(e.target.value)}
                            title={t('common:erd.detailHint')}
                            aria-label={t('common:erd.detailLabel')}
                        >
                            <option value="full">{t('common:erd.detail.full')}</option>
                            <option value="keys">{t('common:erd.detail.keys')}</option>
                            <option value="none">{t('common:erd.detail.none')}</option>
                        </select>

                        <select
                            className={styles.zoomSelect}
                            value={autoFit ? 'auto' : zoomLevel}
                            onChange={handleZoomChange}
                        >
                            <option value="auto">{t('common:erd.autoFit')}</option>
                            <option value="25">25%</option>
                            <option value="50">50%</option>
                            <option value="75">75%</option>
                            <option value="100">100%</option>
                            <option value="125">125%</option>
                            <option value="150">150%</option>
                            <option value="200">200%</option>
                            <option value="300">300%</option>
                            <option value="400">400%</option>
                            <option value="500">500%</option>
                        </select>

                        <button
                            className={styles.controlButton}
                            onClick={handleZoomIn}
                            title={t('common:erd.zoomIn')}
                            disabled={zoomLevel >= 500}
                        >
                            <FaSearchPlus />
                        </button>

                        <button
                            className={styles.controlButton}
                            onClick={handleAutoFit}
                            title={t('common:erd.fitToScreen')}
                        >
                            <FaCompress />
                        </button>
                    </div>

                    <div className={styles.modalControls}>
                        <button
                            className={styles.controlButton}
                            onClick={toggleSourceCode}
                            title={showSourceCode ? t('common:erd.title') : t('common:erd.showSource')}
                        >
                            <FaCode />
                        </button>
                        <button
                            className={styles.controlButton}
                            onClick={handleSaveImage}
                            title={t('common:erd.saveImage')}
                            disabled={showSourceCode}
                        >
                            <FaDownload />
                        </button>
                        {/* 벡터로도 내보낸다 — 큰 ERD 를 인쇄하면 PNG 는 글자가 뭉갠다 */}
                        <button
                            className={styles.controlButton}
                            onClick={handleSaveSvg}
                            title={t('common:erd.saveSvg')}
                            disabled={showSourceCode}
                        >
                            <FaFileImage />
                        </button>
                        <button
                            className={styles.controlButton}
                            onClick={toggleFullscreen}
                            title={isFullscreen ? t('common:erd.normalScreen') : t('common:erd.fullscreen')}
                        >
                            {isFullscreen ? <FaCompressArrowsAlt /> : <FaExpandArrowsAlt />}
                        </button>
                        <button
                            className={styles.closeButton}
                            onClick={() => {
                                // Modal 닫을 때 Mermaid 에러 요소들 정리
                                console.log('ERD Modal closing, cleaning up mermaid elements');
                                const allMermaidElements = document.querySelectorAll('[id*="mermaid"], [id*="erd-diagram"]');
                                allMermaidElements.forEach(element => {
                                    if (!element.closest('.modalOverlay')) {
                                        console.log('Removing mermaid element on modal close:', element.id || element.className);
                                        element.remove();
                                    }
                                });
                                onClose();
                            }}
                            title={t('common:erd.close')}
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>

                <div className={styles.modalBody}>
                    {loading && (
                        <div className={styles.loadingContainer}>
                            <div className={styles.spinner}></div>
                            <p>{t('common:erd.loading')}</p>
                        </div>
                    )}

                    {error && (
                        <div className={styles.errorContainer}>
                            <p className={styles.errorMessage}>{error}</p>
                            <button className={styles.retryButton} onClick={fetchERDData}>
                                {t('common:erd.retry')}
                            </button>
                        </div>
                    )}

                    {!loading && !error && (
                        <div className={styles.bodyLayout}>
                            {/* 왼쪽 테이블 목록 패널 */}
                            <div className={styles.tableListPanel}>
                                <div className={styles.tableListHeader}>
                                    <h3>{t('common:erd.tableList')}</h3>

                                    {/* 스키마 선택 */}
                                    <div className={styles.schemaSelectContainer}>
                                        <label className={styles.schemaLabel}>{t('common:erd.schema')}</label>
                                        <select
                                            value={selectedSchema}
                                            onChange={(e) => handleSchemaChange(e.target.value)}
                                            className={styles.schemaSelect}
                                        >
                                            {availableSchemas.map(schema => (
                                                <option key={schema} value={schema}>
                                                    {schema}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* 테이블 검색 */}
                                    <input
                                        type="text"
                                        placeholder={t('common:erd.searchTable')}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className={styles.searchInput}
                                    />

                                    {/*
                                      주변만 보기 — 큰 스키마에서 ERD 를 되살리는 축은
                                      **표를 줄이는 것**이다(컬럼을 숨기는 것이 아니라).
                                    */}
                                    <div className={styles.hopContainer}>
                                        <label className={styles.schemaLabel}>{t('common:erd.hopTitle')}</label>
                                        <div className={styles.hopRow}>
                                            <select
                                                className={styles.hopSelect}
                                                value={hopCenter}
                                                onChange={(e) => setHopCenter(e.target.value)}
                                            >
                                                <option value="">{t('common:erd.hopCenter')}</option>
                                                {allTables.map(tn => <option key={tn} value={tn}>{tn}</option>)}
                                            </select>
                                            <select
                                                className={styles.hopDepth}
                                                value={hopDepth}
                                                onChange={(e) => setHopDepth(Number(e.target.value))}
                                            >
                                                {[1, 2, 3].map(n => (
                                                    <option key={n} value={n}>{t('common:erd.hopDepth', { n })}</option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                className={styles.hopApply}
                                                onClick={applyNeighborhood}
                                                disabled={!hopCenter}
                                            >
                                                {t('common:erd.hopApply')}
                                            </button>
                                        </div>
                                        <div className={styles.hopHint}>{t('common:erd.hopHint')}</div>
                                    </div>

                                    {/*
                                      추정 관계 — FK 가 선언되지 않은 스키마도 키·인덱스·이름·
                                      운영 SQL 로 관계를 찾는다. **점선**으로 그려 추정임을 남긴다.
                                    */}
                                    <div className={styles.hopContainer}>
                                        <label className={styles.schemaLabel}>{t('common:erd.inferTitle')}</label>
                                        <div className={styles.hopRow}>
                                            <label className={styles.checkboxLabel}>
                                                <input
                                                    type="checkbox"
                                                    className={styles.checkbox}
                                                    checked={showInferred}
                                                    disabled={!inferred.length}
                                                    onChange={(e) => setShowInferred(e.target.checked)}
                                                />
                                                {t('common:erd.inferShow', { n: inferred.length })}
                                            </label>
                                            <button
                                                type="button"
                                                className={styles.hopApply}
                                                onClick={runInference}
                                                disabled={inferring}
                                            >
                                                {inferring ? t('common:erd.inferRunning') : t('common:erd.inferRun')}
                                            </button>
                                        </div>
                                        <div className={styles.hopHint}>
                                            {inferred.length ? t('common:erd.inferHint') : t('common:erd.inferNone')}
                                        </div>
                                    </div>

                                    {/* 모두 선택 */}
                                    <div className={styles.selectAllContainer}>
                                        <label className={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                checked={selectedTables.size === filteredTables.length && filteredTables.length > 0}
                                                onChange={handleSelectAll}
                                                className={styles.checkbox}
                                            />
                                            {t('common:erd.selectAllCount', { n: selectedTables.size, total: allTables.length })}
                                        </label>
                                    </div>
                                </div>
                                <div className={styles.tableList}>
                                    {filteredTables.map((tableName, index) => (
                                        <div
                                            key={tableName}
                                            className={styles.tableItem}
                                            draggable={true}
                                            onDragStart={(e) => handleDragStart(e, tableName, 'table')}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDragEnd={handleDragEnd}
                                            onDrop={(e) => handleDrop(e, tableName, 'table', index)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedTables.has(tableName)}
                                                onChange={() => handleTableToggle(tableName)}
                                                className={styles.checkbox}
                                            />
                                            <span
                                                className={styles.tableName}
                                                onClick={() => {
                                                    console.log('Table name clicked:', tableName);
                                                    // 다이어그램이 완전히 렌더링될 때까지 충분히 대기
                                                    setTimeout(() => handleTableFocus(tableName), 500);
                                                }}
                                                title={t('common:erd.clickToFocus')}
                                            >
                                                {tableName}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* 관계 목록 섹션 */}
                                <div className={styles.tableListHeader}>
                                    <h3>{t('common:erd.relationList')}</h3>

                                    {/* 모든 관계 선택 */}
                                    <div className={styles.selectAllContainer}>
                                        <label className={styles.checkboxLabel}>
                                            <input
                                                type="checkbox"
                                                checked={selectedRelationships.size === allRelationships.length && allRelationships.length > 0}
                                                onChange={handleSelectAllRelationships}
                                                className={styles.checkbox}
                                            />
                                            {t('common:erd.selectAllCount', { n: selectedRelationships.size, total: allRelationships.length })}
                                        </label>
                                    </div>
                                </div>
                                <div className={styles.tableList}>
                                    {allRelationships.map((relationship, index) => (
                                        <div
                                            key={relationship.id}
                                            className={styles.tableItem}
                                            draggable={true}
                                            onDragStart={(e) => handleDragStart(e, relationship.id, 'relationship')}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDragEnd={handleDragEnd}
                                            onDrop={(e) => handleDrop(e, relationship.id, 'relationship', index)}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedRelationships.has(relationship.id)}
                                                onChange={() => handleRelationshipToggle(relationship.id)}
                                                className={styles.checkbox}
                                            />
                                            <span
                                                className={styles.tableName}
                                                title={`${relationship.parentTable} → ${relationship.childTable}`}
                                                onClick={() => {
                                                    addDebugLog('Relationship clicked', {
                                                        id: relationship.id,
                                                        parent: relationship.parentTable,
                                                        child: relationship.childTable,
                                                        display: relationship.displayName
                                                    });
                                                    // 관계선 포커스 (두 테이블 모두 포함하는 영역으로 포커스)
                                                    setTimeout(() => handleRelationshipFocus(relationship), 500);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {relationship.displayName}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* 오른쪽 다이어그램 영역 */}
                            <div className={styles.diagramArea}>
                                {erdDiagram && !showSourceCode && (
                                    <div className={styles.diagramContainer}>
                                        <div
                                            ref={diagramRef}
                                            className={styles.diagramWrapper}
                                            id={diagramId}
                                        />
                                    </div>
                                )}

                                {erdDiagram && showSourceCode && (
                                    <div className={styles.sourceCodeContainer}>
                                        <div className={styles.sourceCodeHeader}>
                                            <h3>{t('common:erd.sourceTitle')}</h3>
                                            <button
                                                className={styles.copyButton}
                                                onClick={async () => {
                                                    try {
                                                        if (navigator.clipboard && navigator.clipboard.writeText) {
                                                            await navigator.clipboard.writeText(erdDiagram);
                                                            console.log('Copied to clipboard via Clipboard API');
                                                        } else {
                                                            // Fallback for older browsers or non-HTTPS
                                                            const textArea = document.createElement('textarea');
                                                            textArea.value = erdDiagram;
                                                            textArea.style.position = 'fixed';
                                                            textArea.style.opacity = '0';
                                                            document.body.appendChild(textArea);
                                                            textArea.select();
                                                            document.execCommand('copy');
                                                            document.body.removeChild(textArea);
                                                            console.log('Copied to clipboard via fallback method');
                                                        }
                                                    } catch (error) {
                                                        console.error('Failed to copy to clipboard:', error);
                                                        toast.error(t('common:erd.copyFailed'));
                                                    }
                                                }}
                                                title={t('common:erd.copyToClipboard')}
                                            >
                                                {t('common:erd.copy')}
                                            </button>
                                        </div>
                                        <pre className={styles.sourceCode}>
                                            <code>{erdDiagram}</code>
                                        </pre>
                                    </div>
                                )}

                                {!erdDiagram && (
                                    <div className={styles.emptyContainer}>
                                        <p>{t('common:erd.noData')}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // 포털을 사용해서 document.body에 렌더링
    return createPortal(modalContent, document.body);
};

ERDModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    schemaName: PropTypes.string,
    connectionData: PropTypes.object,
};

export default ERDModal;